class AuthService {
  constructor () {
    // these values are updated in the `initializeAuth` method
    this._cognito = null
    this._authLoading = null
  }

  /*
  ---------------------------------------------------------------------------------------------------------
  Actions to perform after auth state changes
  ---------------------------------------------------------------------------------------------------------
   */
  /**
   * Setup auth events to occur when the auth state changes.
   * This function should be called once after the app instantiation.
   */
  initializeAuth (appCognitoInstance) {
    // set the app's Cognito instance to this local scope.
    // we need to be listened to the same instance in order to receive the correct auth events
    this._cognito = appCognitoInstance

    // start with a "loading" state for the auth, so our app can wait for the first auth action to be fulfilled
    this._authLoading = true

    // setup listeners to keep track of auth changes
    this.cognito().onAuth((user) => {
      // when the auth state changes, set the state to loading
      this._authLoading = true

      if (user) {
        this.afterLogin()
          .catch((e) => {
            console.error('AuthService.afterLogin - Post-login activity failed')
            console.error(e)
          })
          .then(() => {
            // stop loading
            this._authLoading = false
          })
      } else {
        this.afterLogout()
          .catch((e) => {
            console.error('AuthService.afterLogout - Post-logout activity failed')
            console.error(e)
          })
          .then(() => {
            // stop loading
            this._authLoading = false
          })
      }
    })

    // Load the initial Cognito auth credentials. This will trigger an onAuth event with whatever session
    // credentials are stored in localStorage.
    this._cognito.init()
  }

  /**
   * Wait for the Auth process to complete, including any after-auth actions (such as loading the Campaign ID)
   * @return {Promise<*|Promise<null>>}
   */
  waitForAuth (timeout = 30000) {
    return new Promise((resolve, reject) => {
      // function to check if the auth state is ready and resolve with the logged in user
      const resolveUser = () => {
        this._cognito.cognitoAuthenticatedUser()
          .then(resolve)
          .catch(reject)
      }
      // if the auth state is ready, resolve the user
      if (this._authLoading === false) {
        return resolveUser()
      }
      // otherwise set an interval timer until the designated timeout
      let intervalSpeed = 10
      let reps = Math.ceil(timeout / intervalSpeed)
      const interval = setInterval(() => {
        // if the auth state is ready, resolve the user
        if (this._authLoading === false) {
          resolveUser()
          // stop the interval
          return clearInterval(interval)
        }
        // deduct one rep from the count
        reps--
        // if we are out of reps, then stop the interval
        if (reps === 0) {
          // stop the interval
          clearInterval(interval)
          // reject the parent promise
          reject(Error(`AuthService.waitForAuth timed out after ${timeout}ms`))
        }
      })
    })
  }

  /**
   * Action to perform after login
   * @return {Promise}
   */
  async afterLogin () {
    // actions to handle after the user is logged in
  }

  /**
   * Actions to perform after logout
   */
  async afterLogout () {
    // actions to handle after the user is logged out
  }

  /*
  ---------------------------------------------------------------------------------------------------------
  Request Headers / Middleware
  ---------------------------------------------------------------------------------------------------------
   */
  /**
   * Generate HTTP headers for an API request
   * @return {Promise}
   */
  async generateAuthHeaders () {
    // get the jwt from the Cognito service
    const token = await window.app.$cognito.cognitoJwtToken()
    // build headers based on our JWT token
    const headers = {}
    if (token) {
      headers.Authorization = 'Bearer ' + token
    }
    return headers
  }

  /**
   * Get the Cognito functions from the app instance
   * @return {*}
   */
  cognito () {
    if (!this._cognito) {
      console.error(`AuthService does not have a Cognito instance set. This should be set by calling the "initializeAuth" method before the App is created.`)
    }
    return this._cognito
  }
}

export default new AuthService()
